import { FC, useState, createContext, useContext, useEffect } from 'react';
import { option } from 'fp-ts';
import { FieldType } from 'modules/domains';
import { useCatalogContext } from 'modules/catalog/catalogSelector';
import { noop } from 'utils/noop';
import { useAngularCatalogQueryContext } from '../../contexts/AngularCatalogQueryContext';

type CatalogSelection = {
  isMatchingOnKey: boolean;
  isMatchingOnProperty: boolean;
  hasCatalog: boolean;
  isEnabled: boolean;
  update: (option: boolean) => void;
};

export const defaultCatalogSelection = {
  isMatchingOnKey: false,
  isMatchingOnProperty: false,
  hasCatalog: false,
  isEnabled: false,
  update: noop,
};

export const CatalogSelectionContext = createContext<CatalogSelection>(defaultCatalogSelection);
export const useCatalogSelectionContext = () => useContext(CatalogSelectionContext);

export const CatalogSelectionContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [isMatchingOnKey, update] = useState<boolean>(false);
  const { catalogQueryDetails } = useAngularCatalogQueryContext();
  const { catalog, catalogFieldDetails } = useCatalogContext();
  const isMatchingOnProperty = option.isSome(catalogFieldDetails);
  const hasCatalog = option.isSome(catalog);
  const searchFieldType = catalogQueryDetails?.fieldType ?? FieldType.UNDEFINED;

  const isEnabled = hasCatalog && (isMatchingOnKey || isMatchingOnProperty); // check to see if query builder is enabled;

  // update isMatchingOnKey if searchFieldType doesn't match String
  useEffect(() => {
    if (catalogQueryDetails && searchFieldType !== FieldType.STRING) {
      update(false);
    }
  }, [searchFieldType]);

  const value = {
    isMatchingOnKey,
    isMatchingOnProperty,
    hasCatalog,
    update,
    isEnabled,
  };

  return (
    <CatalogSelectionContext.Provider value={value}>{children}</CatalogSelectionContext.Provider>
  );
};
